import { useContext, useEffect, useMemo, useState } from 'react';
import { Flame, Plus, Save, Sparkles, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { AuthContext } from '../../contexts/AuthContext';
import { listUserProfilesByName } from '../../database/userProfiles';
import {
  addTier,
  deleteTier,
  getTiers,
  getAllPlayerSpend,
  seedDefaultTiers,
  setPlayerSpend,
  updateTier,
} from '../../firebase/supporterTiers';
import { TIER_ACCENTS, resolveTier } from '../../constants/supporterTiers';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { NativeSelect } from '../ui/select-native';
import TierBadge from '../ui/TierBadge';
import PageHeader from './shell/PageHeader';

const Field = ({ label, required, hint, children }) => (
  <div className="flex flex-col gap-2">
    <span className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
      {label}
      {required && <span className="ml-1 text-heat">*</span>}
    </span>
    {children}
    {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
  </div>
);

const ACCENT_KEYS = Object.keys(TIER_ACCENTS);

const blankTier = { name: '', minSpend: '', accent: 'heat', glow: false };

const SupporterTierManagement = () => {
  const { currentUser } = useContext(AuthContext);

  const [tiers, setTiers] = useState([]);
  const [users, setUsers] = useState([]);
  const [spendMap, setSpendMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingTier, setSavingTier] = useState(false);
  const [draft, setDraft] = useState(blankTier);
  const [editId, setEditId] = useState(null);
  const [deleteTierTarget, setDeleteTierTarget] = useState(null);

  const [spendForm, setSpendForm] = useState({ userId: '', amount: '', note: '' });
  const [savingSpend, setSavingSpend] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [tierList, users, spend] = await Promise.all([
        getTiers(),
        listUserProfilesByName(),
        getAllPlayerSpend(),
      ]);
      setTiers(tierList);
      setUsers(users);
      setSpendMap(spend);
    } catch (err) {
      toast.error('Could not load supporter data.', { description: err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const resetTierDraft = () => {
    setDraft(blankTier);
    setEditId(null);
  };

  const handleSaveTier = async (e) => {
    e.preventDefault();
    if (!draft.name.trim()) {
      toast.error('Give the tier a name.');
      return;
    }
    setSavingTier(true);
    try {
      if (editId) {
        await updateTier(editId, draft);
        toast.success('Tier updated.');
      } else {
        await addTier(draft);
        toast.success('Tier added.');
      }
      resetTierDraft();
      setTiers(await getTiers());
    } catch (err) {
      toast.error('Could not save tier.', { description: err.message });
    } finally {
      setSavingTier(false);
    }
  };

  const handleEditTier = (t) => {
    setEditId(t.id);
    setDraft({ name: t.name, minSpend: t.minSpend, accent: t.accent || 'heat', glow: !!t.glow });
  };

  const handleDeleteTier = async () => {
    if (!deleteTierTarget) return;
    try {
      await deleteTier(deleteTierTarget.id);
      toast.success('Tier removed.');
      if (editId === deleteTierTarget.id) resetTierDraft();
      setDeleteTierTarget(null);
      setTiers(await getTiers());
    } catch (err) {
      toast.error('Delete failed.', { description: err.message });
    }
  };

  const handleSeed = async () => {
    try {
      const seeded = await seedDefaultTiers();
      if (seeded) {
        toast.success('Default tiers created.');
        setTiers(await getTiers());
      } else {
        toast.info('Tiers already exist — nothing seeded.');
      }
    } catch (err) {
      toast.error('Seeding failed.', { description: err.message });
    }
  };

  const handleSaveSpend = async (e) => {
    e.preventDefault();
    if (!spendForm.userId) {
      toast.error('Pick a player.');
      return;
    }
    setSavingSpend(true);
    try {
      const amount = parseFloat(spendForm.amount) || 0;
      await setPlayerSpend(spendForm.userId, amount, {
        note: spendForm.note,
        updatedBy: currentUser?.uid || null,
      });
      setSpendMap((m) => ({ ...m, [spendForm.userId]: amount }));
      toast.success('Spend updated.');
      setSpendForm({ userId: '', amount: '', note: '' });
    } catch (err) {
      toast.error('Could not save spend.', { description: err.message });
    } finally {
      setSavingSpend(false);
    }
  };

  const userName = (id) => users.find((u) => u.id === id)?.username || id;

  // Players with a recorded spend, highest first.
  const backers = useMemo(
    () =>
      Object.entries(spendMap)
        .map(([id, amount]) => ({ id, amount, tier: resolveTier(amount, tiers) }))
        .filter((b) => b.amount > 0)
        .sort((a, b) => b.amount - a.amount),
    [spendMap, tiers]
  );

  // Preview the tier the current amount in the form would resolve to.
  const previewTier = resolveTier(parseFloat(spendForm.amount) || 0, tiers);

  return (
    <div className="pb-16">
      <PageHeader
        eyebrow="Tournaments"
        title="Supporters & tiers"
        description="Record how much each player has contributed to the prize pool, and manage the spend tiers that show on their profile."
      />

      {/* ---- TIERS -------------------------------------------------------- */}
      <Card className="border-border/60 bg-background/40">
        <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
          <CardTitle className="flex items-center gap-2 font-display text-lg">
            <Flame className="h-4 w-4 text-heat" />
            Supporter tiers
          </CardTitle>
          {tiers.length === 0 && !loading && (
            <Button variant="secondary" size="sm" onClick={handleSeed}>
              <Sparkles className="h-3.5 w-3.5" />
              Create default tiers
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-6">
          {tiers.length > 0 && (
            <ul className="divide-y divide-border/60 border-y border-border/60">
              {tiers.map((t) => (
                <li key={t.id} className="flex items-center gap-4 px-1 py-3">
                  <TierBadge tier={t} size="lg" />
                  <span className="font-mono text-xs tabular-nums text-muted-foreground">
                    ≥ €{t.minSpend}
                  </span>
                  <div className="ml-auto flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleEditTier(t)}>
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setDeleteTierTarget(t)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <form onSubmit={handleSaveTier} className="grid gap-5 md:grid-cols-4">
            <Field label="Tier name" required>
              <Input
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="e.g. Ember"
              />
            </Field>
            <Field label="Reaches at" hint="EUR, minimum spend">
              <Input
                type="number"
                min="0"
                step="1"
                value={draft.minSpend}
                onChange={(e) => setDraft({ ...draft, minSpend: e.target.value })}
                placeholder="50"
              />
            </Field>
            <Field label="Accent">
              <NativeSelect
                value={draft.accent}
                onChange={(e) => setDraft({ ...draft, accent: e.target.value })}
              >
                {ACCENT_KEYS.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field label="Animated glow">
              <label className="flex h-10 cursor-pointer items-center gap-2 text-sm text-muted-foreground">
                <input
                  type="checkbox"
                  className="h-4 w-4 cursor-pointer"
                  checked={draft.glow}
                  onChange={(e) => setDraft({ ...draft, glow: e.target.checked })}
                />
                Glow halo
              </label>
            </Field>
            <div className="flex items-center gap-3 md:col-span-4">
              <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground">
                Preview
              </span>
              {draft.name.trim() ? (
                <TierBadge
                  tier={{ name: draft.name, accent: draft.accent, glow: draft.glow }}
                  size="lg"
                />
              ) : (
                <span className="text-xs text-muted-foreground">— enter a name —</span>
              )}
              <div className="ml-auto flex gap-2">
                {editId && (
                  <Button type="button" variant="ghost" onClick={resetTierDraft}>
                    Cancel
                  </Button>
                )}
                <Button type="submit" disabled={savingTier}>
                  {editId ? <Save className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                  {savingTier ? 'Saving…' : editId ? 'Update tier' : 'Add tier'}
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* ---- PLAYER SPEND ------------------------------------------------- */}
      <Card className="mt-8 border-border/60 bg-background/40">
        <CardHeader>
          <CardTitle className="font-display text-lg">Set player spend</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSaveSpend} className="grid gap-5 md:grid-cols-3">
            <Field label="Player" required>
              <NativeSelect
                value={spendForm.userId}
                onChange={(e) =>
                  setSpendForm({
                    ...spendForm,
                    userId: e.target.value,
                    amount:
                      spendMap[e.target.value] != null ? String(spendMap[e.target.value]) : '',
                  })
                }
              >
                <option value="">— Select player —</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.username || u.id}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field label="Total spend" hint="EUR — total contributed to date">
              <Input
                type="number"
                min="0"
                step="0.01"
                value={spendForm.amount}
                onChange={(e) => setSpendForm({ ...spendForm, amount: e.target.value })}
                placeholder="0"
              />
            </Field>
            <Field label="Note" hint="optional, admin-only">
              <Input
                value={spendForm.note}
                onChange={(e) => setSpendForm({ ...spendForm, note: e.target.value })}
                placeholder="e.g. 2025 season backer"
              />
            </Field>
            <div className="flex items-center gap-3 md:col-span-3">
              {previewTier ? (
                <span className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-muted-foreground">
                  Resolves to <TierBadge tier={previewTier} size="lg" />
                </span>
              ) : (
                <span className="text-xs text-muted-foreground">
                  Below the first tier — no badge shown.
                </span>
              )}
              <Button type="submit" disabled={savingSpend} className="ml-auto">
                <Save className="h-4 w-4" />
                {savingSpend ? 'Saving…' : 'Save spend'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* ---- BACKERS LIST ------------------------------------------------- */}
      <section className="mt-12">
        <div className="mb-4 flex items-center gap-4">
          <span className="h-px w-6 bg-heat" />
          <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
            Backers · {backers.length}
          </h2>
        </div>
        {loading ? (
          <div className="divide-y divide-border/60 border-y border-border/60">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-14 animate-pulse bg-muted/10" />
            ))}
          </div>
        ) : backers.length === 0 ? (
          <div className="border border-dashed border-border/60 px-6 py-12 text-center text-sm text-muted-foreground">
            No player spend recorded yet.
          </div>
        ) : (
          <ul className="divide-y divide-border/60 border-y border-border/60">
            {backers.map((b) => (
              <li
                key={b.id}
                className="grid grid-cols-[1fr_auto_auto] items-center gap-4 px-1 py-3"
              >
                <span className="truncate font-display text-base font-medium">
                  {userName(b.id)}
                </span>
                {b.tier ? (
                  <TierBadge tier={b.tier} size="sm" />
                ) : (
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    no tier
                  </span>
                )}
                <span className="font-mono text-xs tabular-nums text-foreground/80">
                  €{b.amount}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <Dialog open={!!deleteTierTarget} onOpenChange={(o) => !o && setDeleteTierTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Remove tier?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Removes the{' '}
            <span className="font-medium text-foreground">{deleteTierTarget?.name}</span> tier.
            Players in this band will drop to the next-lowest tier they qualify for.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteTierTarget(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteTier}>
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupporterTierManagement;
